import { loadDeviceSelection, restoreOrAutoSelectDevice, saveDeviceSelection } from '@/utils/deviceHelpers'
import { useCallback, useEffect, useState, useRef } from 'react'

export type DeviceInfo = {
  id: string
  label: string
}

export type UseBrowserDevicesResult = {
  // Device lists
  audioInputs: DeviceInfo[]
  audioOutputs: DeviceInfo[]
  videoInputs: DeviceInfo[]
  // Selected devices
  selectedAudioInput: string | undefined
  selectedAudioOutput: string | undefined
  selectedVideoInput: string | undefined
  // Selection functions
  selectAudioInput: (params: { id: string }) => Promise<void>
  selectAudioOutput: (params: { id: string }) => Promise<void>
  selectVideoInput: (params: { id: string }) => Promise<void>
  // Refresh function
  refreshDevices: () => Promise<void>
  // State
  isRefreshing: boolean
  audioError: string | undefined
  videoError: string | undefined
}

/**
 * Hook for browser-native device enumeration and selection
 * Works without a Chime meeting session
 */
export function useBrowserDevices(): UseBrowserDevicesResult {
  const [audioInputs, setAudioInputs] = useState<DeviceInfo[]>([])
  const [audioOutputs, setAudioOutputs] = useState<DeviceInfo[]>([])
  const [videoInputs, setVideoInputs] = useState<DeviceInfo[]>([])

  const [selectedAudioInput, setSelectedAudioInput] = useState<string | undefined>(undefined)
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string | undefined>(undefined)
  const [selectedVideoInput, setSelectedVideoInput] = useState<string | undefined>(undefined)

  const [audioError, setAudioError] = useState<string | undefined>(undefined)
  const [videoError, setVideoError] = useState<string | undefined>(undefined)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const hasRequestedPermissions = useRef(false)
  const hasInitialized = useRef(false)

  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()

      const audioIn = devices
        .filter((d) => d.kind === 'audioinput' && d.deviceId && d.deviceId.trim() !== '')
        .map((d, index) => ({
          id: d.deviceId,
          label: d.label || `Microphone ${index + 1}`
        }))

      const audioOut = devices
        .filter((d) => d.kind === 'audiooutput' && d.deviceId && d.deviceId.trim() !== '')
        .map((d, index) => ({
          id: d.deviceId,
          label: d.label || `Speaker ${index + 1}`
        }))

      const videoIn = devices
        .filter((d) => d.kind === 'videoinput' && d.deviceId && d.deviceId.trim() !== '')
        .map((d, index) => ({
          id: d.deviceId,
          label: d.label || `Camera ${index + 1}`
        }))

      setAudioInputs(audioIn)
      setAudioOutputs(audioOut)
      setVideoInputs(videoIn)

      // Clear invalid selections (empty or not in the list)
      const validateSelection = (prev: string | undefined, devices: DeviceInfo[]) => {
        if (!prev || prev.trim() === '' || !devices.some((d) => d.id === prev)) {
          return undefined
        }
        return prev
      }

      setSelectedAudioInput((prev) => validateSelection(prev, audioIn))
      setSelectedAudioOutput((prev) => validateSelection(prev, audioOut))
      setSelectedVideoInput((prev) => validateSelection(prev, videoIn))

      return { audioIn, audioOut, videoIn }
    } catch (err) {
      console.error('[useBrowserDevices] Failed to enumerate devices:', err)
      return { audioIn: [], audioOut: [], videoIn: [] }
    }
  }, [])

  const requestPermissionsAndEnumerate = useCallback(async () => {
    if (hasRequestedPermissions.current) {
      return enumerateDevices()
    }

    setIsRefreshing(true)
    setAudioError(undefined)
    setVideoError(undefined)

    try {
      // Request permissions to get device labels
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true
      })

      // Stop the stream immediately - we just needed permissions
      stream.getTracks().forEach((track) => track.stop())

      hasRequestedPermissions.current = true

      // Now enumerate devices with labels
      const { audioIn, audioOut, videoIn } = await enumerateDevices()

      // Validate and restore saved device selections
      const saved = loadDeviceSelection()

      // Restore or auto-select devices
      setSelectedAudioInput((current) =>
        restoreOrAutoSelectDevice(saved.audioInput, audioIn, current)
      )

      setSelectedAudioOutput((current) =>
        restoreOrAutoSelectDevice(saved.audioOutput, audioOut, current)
      )

      setSelectedVideoInput((current) =>
        restoreOrAutoSelectDevice(saved.videoInput, videoIn, current)
      )

      return { audioIn, audioOut, videoIn }
    } catch (err) {
      console.error('[useBrowserDevices] Permission error:', err)

      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setAudioError('Accès au microphone refusé')
          setVideoError('Accès à la caméra refusé')
        } else if (err.name === 'NotFoundError') {
          // Try audio only
          try {
            const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true })
            audioStream.getTracks().forEach((track) => track.stop())
          } catch (audioErr) {
            console.error('[useBrowserDevices] Error getting audio-only stream:', audioErr);
            setAudioError('Aucun microphone trouvé')
          }

          // Try video only
          try {
            const videoStream = await navigator.mediaDevices.getUserMedia({ video: true })
            videoStream.getTracks().forEach((track) => track.stop())
          } catch (videoErr) {
            console.error('[useBrowserDevices] Error getting video-only stream:', videoErr);
            setVideoError('Aucune caméra trouvée')
          }
        }
      }

      // Still enumerate what we can
      return enumerateDevices()
    } finally {
      setIsRefreshing(false)
    }
  }, [enumerateDevices])

  // Initial setup
  useEffect(() => {
    void requestPermissionsAndEnumerate().then(() => {
      // Mark as initialized after first enumeration completes
      hasInitialized.current = true
    })
  }, [])

  // Save device selections to localStorage when they change (after initialization)
  useEffect(() => {
    // Don't save during initial load/restoration
    if (!hasInitialized.current) {
      return
    }

    // Save selections whenever they change (after initialization)
    saveDeviceSelection({
      audioInput: selectedAudioInput,
      audioOutput: selectedAudioOutput,
      videoInput: selectedVideoInput
    })
  }, [selectedAudioInput, selectedAudioOutput, selectedVideoInput])

  // Listen for device changes
  useEffect(() => {
    const handleDeviceChange = () => {
      void enumerateDevices()
    }

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange)
    }
  }, [enumerateDevices])

  const selectAudioInput = useCallback(async ({ id }: { id: string }) => {
    setSelectedAudioInput(id)
    setAudioError(undefined)
  }, [])

  const selectAudioOutput = useCallback(async ({ id }: { id: string }) => {
    setSelectedAudioOutput(id)
  }, [])

  const selectVideoInput = useCallback(async ({ id }: { id: string }) => {
    setSelectedVideoInput(id)
    setVideoError(undefined)
  }, [])

  const refreshDevices = useCallback(async () => {
    setIsRefreshing(true)
    await enumerateDevices()
    setIsRefreshing(false)
  }, [enumerateDevices])

  return {
    audioInputs,
    audioOutputs,
    videoInputs,
    selectedAudioInput,
    selectedAudioOutput,
    selectedVideoInput,
    selectAudioInput,
    selectAudioOutput,
    selectVideoInput,
    refreshDevices,
    isRefreshing,
    audioError,
    videoError
  }
}

